'use client';

import type { Options } from '@contentful/rich-text-react-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import clsx from 'clsx';
import Image from 'next/image';
import type { ReactNode } from 'react';

import { useContactInfo } from '@/hooks/useContactInfo';
import { useContactTracking } from '@/hooks/useContactTracking';
import {
  CookiePolicyBlock,
  hasCookiePolicyPlaceholder,
  hasDynamicPlaceholders,
  processTextWithPlaceholders,
  getParagraphTextValue,
} from '@/lib/cms/richTextDynamicValues';
import { ensureTrailingSlash } from '@/lib/utils/ensureTrailingSlash';
import styles from './FreeTextContent.module.scss';

/**
 * Asset links type from Contentful
 */
export interface FreeTextContentLinks {
  assets?: {
    block?: Array<{
      sys: { id: string };
      url?: string | null;
    } | null>;
  };
}

export interface FreeTextContentProps {
  /** Rich text content from Contentful */
  freeTextContent?: {
    json?: unknown;
    links?: FreeTextContentLinks;
  } | null;
  /** Optional className */
  className?: string;
  /** Contact info for dynamic value injection */
  contactInfo?: {
    email?: string;
    phoneNumber?: string;
  };
  /** CMS component index for E2E test targeting */
  cmsIndex?: number;
}

/**
 * Create render options for rich text
 */
function createRenderOptions(
  links?: FreeTextContentLinks,
  contactInfo?: { email?: string; phoneNumber?: string },
  onPhoneClick?: () => void,
  onEmailClick?: () => void
): Options {
  // Build asset map for embedded assets
  const assetMap = new Map<string, { url: string }>();
  if (links?.assets?.block) {
    for (const asset of links.assets.block) {
      if (asset?.sys?.id && asset.url) {
        assetMap.set(asset.sys.id, { url: asset.url });
      }
    }
  }

  const options: Options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (hasCookiePolicyPlaceholder(node)) {
          return <CookiePolicyBlock node={node} options={options} />;
        }

        if (hasDynamicPlaceholders(node)) {
          return (
            <p className={styles.paragraph}>
              {processTextWithPlaceholders(getParagraphTextValue(node), {
                contactInfo,
                onPhoneClick,
                onEmailClick,
                linkClassName: styles.link,
                cookieButtonClassName: styles.cookieButton,
              })}
            </p>
          );
        }

        return <p className={styles.paragraph}>{children}</p>;
      },

      [BLOCKS.HEADING_1]: (_node, children) => (
        <h1 className={clsx(styles.heading, styles.h1)}>{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (_node, children) => (
        <h2 className={clsx(styles.heading, styles.h2)}>{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (_node, children) => (
        <h3 className={clsx(styles.heading, styles.h3)}>{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (_node, children) => (
        <h4 className={clsx(styles.heading, styles.h4)}>{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (_node, children) => (
        <h5 className={clsx(styles.heading, styles.h5)}>{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (_node, children) => (
        <h6 className={clsx(styles.heading, styles.h6)}>{children}</h6>
      ),

      [INLINES.HYPERLINK]: (node, children) => {
        const uri = node.data.uri as string;
        const isExternal = uri.startsWith('http://') || uri.startsWith('https://');
        const href = isExternal ? uri : ensureTrailingSlash(uri);
        return (
          <a
            href={href}
            className={styles.link}
            target={isExternal ? '_blank' : '_self'}
            rel={isExternal ? 'noopener noreferrer' : undefined}
          >
            {children}
          </a>
        );
      },

      [BLOCKS.UL_LIST]: (_node, children) => <ul className={styles.list}>{children}</ul>,
      [BLOCKS.OL_LIST]: (_node, children) => (
        <ol className={clsx(styles.list, styles.orderedList)}>{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (_node, children) => <li>{children}</li>,

      [BLOCKS.QUOTE]: (_node, children) => (
        <blockquote className={styles.quote}>{children}</blockquote>
      ),

      [BLOCKS.HR]: () => <hr className={styles.hr} />,

      [BLOCKS.TABLE]: (_node, children) => (
        <table className={styles.table}>
          <tbody>{children}</tbody>
        </table>
      ),
      [BLOCKS.TABLE_ROW]: (_node, children) => <tr>{children}</tr>,
      [BLOCKS.TABLE_CELL]: (_node, children) => <td>{children}</td>,
      [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => <th>{children}</th>,

      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const assetId = node.data.target?.sys?.id;
        const asset = assetId ? assetMap.get(assetId) : null;

        if (asset?.url) {
          return (
            <div className={styles.embeddedImageDesktop}>
              <Image
                src={asset.url}
                alt="Embedded content"
                width={600}
                height={400}
                className={styles.embeddedImage}
              />
            </div>
          );
        }
        return null;
      },
    },

    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text) => <em>{text}</em>,
      [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
      [MARKS.CODE]: (text) => <code>{text}</code>,
    },

    renderText: (text) => {
      // Handle line breaks in text
      return text.split('\n').reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, [] as ReactNode[]);
    },
  };

  return options;
}

/**
 * FreeTextContent component
 *
 * Renders Contentful rich text content with proper styling and dynamic value injection.
 * Supports headings, paragraphs, lists, tables, quotes, embedded images, and links.
 *
 * @example
 * <FreeTextContent
 *   freeTextContent={{
 *     json: richTextDocument,
 *     links: { assets: { block: [...] } }
 *   }}
 *   contactInfo={{ email: 'info@example.com', phoneNumber: '+1234567890' }}
 * />
 */
export function FreeTextContent({
  freeTextContent,
  className,
  contactInfo: contactInfoProp,
  cmsIndex,
}: FreeTextContentProps) {
  const hookContactInfo = useContactInfo();
  const { trackPhoneContact, trackEmailContact } = useContactTracking();
  const contactInfo = {
    phoneNumber: contactInfoProp?.phoneNumber ?? hookContactInfo.phoneNumber,
    email: contactInfoProp?.email ?? hookContactInfo.email,
  };

  // Generate testid prefix for E2E tests
  const testIdPrefix = cmsIndex !== undefined ? `freetextcontent-${cmsIndex}` : undefined;

  if (!freeTextContent?.json) {
    return null;
  }

  const options = createRenderOptions(
    freeTextContent.links ?? undefined,
    contactInfo,
    trackPhoneContact,
    trackEmailContact
  );

  return (
    <div className={clsx(styles.wrapper, className)} data-testid={testIdPrefix}>
      {documentToReactComponents(
        freeTextContent.json as Parameters<typeof documentToReactComponents>[0],
        options
      )}
    </div>
  );
}

FreeTextContent.displayName = 'FreeTextContent';
